import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { FaArrowRight, FaCube, FaFileAlt } from "react-icons/fa";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "./AuthContext";
import {
  CUSTOM_DESIGN_API_BASE,
  projectStatusTranslationKeys,
  readApiError,
  translateDesignValue,
} from "./customDesignUtils";
import "./CustomProjects.css";

const CustomProjects = () => {
  const { user, loading } = useAuth();
  const { t, formatDate } = useI18n();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      if (!loading) setProjectsLoading(false);
      return;
    }
    let active = true;
    const load = async () => {
      setProjectsLoading(true);
      setError("");
      try {
        const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error(await readApiError(response, t("design.projects.error")));
        const data = await response.json();
        if (active) setProjects(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (active) setError(t("design.projects.error"));
      } finally {
        if (active) setProjectsLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [user, loading, t]);

  if (!loading && !user) {
    return <Navigate to="/login?next=%2Faccount%2Fcustom-projects" replace />;
  }

  return (
    <main className="custom-projects-page">
      <Seo title={t("design.projects.seoTitle")} description={t("design.projects.seoDescription")} path="/account/custom-projects" noindex />
      <header className="custom-projects-header">
        <div>
          <p>{t("design.projects.kicker")}</p>
          <h1>{t("design.projects.title")}</h1>
          <span>{t("design.projects.description")}</span>
        </div>
        <Link to="/custom-design">{t("design.projects.start")} <FaArrowRight /></Link>
      </header>

      {error && <div className="custom-projects-alert">{error}</div>}
      {projectsLoading ? <div className="custom-projects-empty">{t("design.projects.loading")}</div> : null}
      {!projectsLoading && !projects.length ? (
        <section className="custom-projects-empty">
          <FaCube />
          <h2>{t("design.projects.emptyTitle")}</h2>
          <p>{t("design.projects.emptyDescription")}</p>
          <Link to="/custom-design">{t("design.projects.startFirst")}</Link>
        </section>
      ) : null}

      <section className="custom-projects-grid">
        {projects.map((project) => (
          <Link className="custom-project-card" to={`/account/custom-projects/${project.id}`} key={project.id}>
            <div className="custom-project-card__top">
              <span className={`custom-project-status status-${project.status}`}>
                {translateDesignValue(t, projectStatusTranslationKeys, project.status)}
              </span>
              <small>{project.id}</small>
            </div>
            <h2>{project.title}</h2>
            <p>{project.description}</p>
            <div className="custom-project-card__meta">
              <span><FaFileAlt /> {t("design.projects.files", { count: project.fileCount || 0 })}</span>
              <span>{t("design.projects.messages", { count: project.messageCount || 0 })}</span>
            </div>
            <footer>
              <span>{t("design.projects.updated", { date: formatDate(project.updatedAt, { dateStyle: "medium", timeStyle: "short" }) })}</span>
              <FaArrowRight />
            </footer>
          </Link>
        ))}
      </section>
    </main>
  );
};

export default CustomProjects;
